import { PropertyValueMap, TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksRadioChangeEvent } from '../../events/blocks-radio-change';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import type { StatusType } from './radio-field.model';
import styles from './radio-field.styles';

/**
 * @slot default - The component content
 * @slot error - error slot
 * @slot field-note - field-note slot
 * @slot warning - warningNote slot
 *
 * @fires bl-radio-change - Emitted when radio field is selected.
 *
 * @csspart base - The component's base wrapper.
 * @csspart container - The component's container wrapper.
 * @csspart input - The component's input element.
 * @csspart custom-wrapper - The component's custom wrapper field.
 * @csspart custom-radio - The component's custom radio field.
 * @csspart part-label - The component's label element.
 * @csspart part-default- The component's default slot.
 * @csspart part-notation - The component's label notation wrapper
 * @csspart loader - The component's skeleton loader element.
 * @csspart field-note-loader - The component's skeleton field note loader element.
 * @csspart field-note-skeleton - The component's skeleton field note skeleton element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart warning-icon - The component's warning icon.
 * @csspart part-error - The component's error slot.
 * @csspart error-icon - The component's error icon.
 */
export class BlocksRadioField extends BlocksElement {
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * Status of radio-field -  warning or error
   */
  @property({ type: String })
  status: StatusType;

  /**
   *  Error message for the field
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   *  Warning message for the field
   */
  @property({ type: String })
  warningNote: string | undefined;

  /**
   * Description for the field
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * readOnly attribute
   */
  @property({ type: Boolean })
  readOnly = false;

  /**
   * disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * value attribute
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Id
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * Name attribute
   */
  @property({ type: String, reflect: true })
  name: string | undefined;

  /**
   * The radio field's label
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * Notation is used for changing required text or optional label
   */
  @property({ type: String })
  notation: string | undefined;

  /**
   * Checked property
   */
  @property({ type: Boolean, reflect: true })
  checked = false;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required: boolean | undefined;

  /**
   * Hide label?
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * isInline property
   * When true, this property overrides the default flex behavior, aligning the element inline instead of filling the container width.
   */
  @property({ type: Boolean })
  isInline = false;

  /**
   * Loading state
   * 1) Adds a loader if is true
   */
  @property({ type: Boolean })
  isLoading: boolean | undefined;

  /**
   * Indicates that the radio-field should have input focus when the page loads.
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Name or Title for the warning icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  warningIconTitle = 'warning icon';

  /**
   * Name or Title for the error icon
   * meant for accessibility purposes only
   */
  @property({ type: String })
  errorIconTitle = 'error icon';

  /**
   * Indicates that the radio-field is in pressed state.
   */
  @state()
  private radioPressed = false;

  /**
   * Queries the radio container of the radio field
   * @ignore
   */
  @query('.cat-c-radio-field') radioFieldParentWrapper!: HTMLDivElement;

  protected slots = new SlotController(this, null, 'error', 'field-note', 'warning');

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }

  private get hasErrorSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  private get hasWarningSlot(): boolean {
    return this.slots.hasSlotted('warning');
  }

  /**
   * Query all radio button with same name
   */
  private get getAllNamedSiblings(): BlocksRadioField[] {
    return Array.from((this.getRootNode() as Document | ShadowRoot).querySelectorAll<BlocksRadioField>(`cat-radio-field[name="${this.name}"]`));
  }

  /**
   * Query for any radio group item checked
   */
  private get radioItemsChecked() {
    return Array.from((this.getRootNode() as Document).querySelectorAll<BlocksRadioField>(`cat-radio-field[name="${this.name}"]`)).some(
      (el) => el.checked === true
    );
  }

  private internals_ = this.attachInternals?.();

  public type = 'radio';

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * Autogenerate
   */
  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
  }

  firstUpdated() {
    this.updateTabIndex();
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('value') || _changedProperties.has('checked')) {
      this.internals_?.setFormValue(this.checked ? (this.value ?? null) : null);
    }
    if (_changedProperties.has('checked')) {
      this.updateTabIndex();
    }
    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  /**
   * Remove checked
   * 1) Remove checked property from all items and set tabindex to -1
   */
  private unCheckAll = () => {
    /* 1 */
    this.getAllNamedSiblings.forEach((element: BlocksRadioField) => {
      if (!element.readOnly) {
        element.checked = false;
        element.radioFieldParentWrapper?.setAttribute('tabindex', '-1');
      }
    });
  };

  /**
   * Set the tabindex value to 0 for the checked radio field or no radio item is checked
   * otherwise set tabindex -1
   * 1) radioItemsChecked - In case the user is updating the checked status to false and none of the radio group item is checked then make the tabindex to 0 so the focus will be available for first clickable radio
   */
  private async updateTabIndex(): Promise<void> {
    await this.updateComplete;
    this.setTabIndexForRadioInputs();
  }

  /**
   * Sets the tabindex attribute for radio inputs within the named siblings.
   *
   * This function iterates through all named siblings and sets the tabindex attribute
   * of their corresponding radio inputs to either '0' (focusable) or '-1' (not focusable).
   * The tabindex is set to '0' only for the radio input at the index matching the first enabled radio button.
   *
   */
  private setTabIndexForRadioInputs() {
    const firstEnabledRadioIndex = this.findEnabledRadioIndex();

    if (firstEnabledRadioIndex !== -1) {
      this.getAllNamedSiblings.forEach((element, index) => {
        element.radioFieldParentWrapper?.setAttribute('tabindex', index === firstEnabledRadioIndex ? '0' : '-1');
      });
    }
  }

  /**
   * Finds the index of the first enabled radio button within the named siblings.
   * @private
   * @returns {number} The index of the first enabled radio button, or -1 if none are found.
   */
  private findEnabledRadioIndex() {
    const nonDisabledCheckedRadio = this.getAllNamedSiblings.findIndex((el) => !el.disabled && el.checked);
    const firstNonDisabledRadio = this.getAllNamedSiblings.findIndex((el) => !el.disabled);
    return nonDisabledCheckedRadio > -1 ? nonDisabledCheckedRadio : firstNonDisabledRadio;
  }

  /**
   * Toggle active state of primary nav item
   * 1) Remove isActive state from all sibling elements
   * 2) Toggle active state of element selected
   */
  private handleRadioSelection = () => {
    this.unCheckAll();
    this.checked = true; /* 2 */

    this.dispatchRadioChangeEvent();
  };

  /**
   * Dispatch radio change event
   */
  private dispatchRadioChangeEvent() {
    this.dispatch({ eventName: 'bl-radio-change', detailObj: { name: this.name, value: this.value } }) as BlocksRadioChangeEvent;
  }

  /**
   * Handle keydown
   * 1) To prevent default click in readonly state on pressing SPACE
   */
  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === KEYS.LEFT_ARROW || e.key === KEYS.UP_ARROW) {
      this.activatePreviousRadioButton();
    } else if (e.key === KEYS.RIGHT_ARROW || e.key === KEYS.DOWN_ARROW) {
      this.activateNextRadioButton();
    }
    if (e.key === KEYS.ENTER && !this.radioItemsChecked && !this.readOnly && !this.checked) {
      this.handleRadioSelection();
    }
    if (e.key === KEYS.SPACE && !this.readOnly) {
      this.radioPressed = true;
    }
    if (this.readOnly && e.key === KEYS.SPACE) {
      e.preventDefault();
    }
  };

  /**
   * Handle Keyup
   * 1) To have bg-color on input radio on pressed state
   */
  private handleKeyUp(e: KeyboardEvent) {
    if (e.key === KEYS.SPACE) {
      this.radioPressed = false;
      if (!this.radioItemsChecked && !this.readOnly && !this.checked) {
        this.handleRadioSelection();
      }
    }
  }

  override focus(): void {
    this.radioFieldParentWrapper?.focus();
  }

  private activatePreviousRadioButton(): void {
    /* 1 */
    const index = this.findPreviousRadioButtonIndex();
    const previousElement = this.getAllNamedSiblings[index];
    if (previousElement) {
      const radioElement = previousElement.radioFieldParentWrapper;
      radioElement.setAttribute('tabindex', '0');
      radioElement.focus();
      if (!previousElement.readOnly) {
        this.unCheckAll();
        previousElement.checked = true;
        this.dispatchRadioChangeEvent();
      }
    }
  }

  /**
   * 1) Find current active radio button and move the active status to previous button
   * 2) If no more previous radio button means move active status to last radio button
   * 3) Check whether previous radio is disabled or not if it is disabled move active state to previously enabled radio
   */
  private findPreviousRadioButtonIndex(): number {
    const radioGroupLength = this.getAllNamedSiblings.length;
    let index = this.getAllNamedSiblings.indexOf(this); /**1 */
    index = index - 1;
    if (index < 0) {
      /**2 */
      index = radioGroupLength - 1;
    }
    /**3 */
    if (this.getAllNamedSiblings[index]) {
      while (this.getAllNamedSiblings[index].disabled) {
        if (index === 0) {
          index = radioGroupLength - 1;
        } else {
          index = index - 1;
        }
      }
    }
    return index;
  }

  private activateNextRadioButton(): void {
    const index = this.findNextRadioButtonIndex();
    const nextElement = this.getAllNamedSiblings[index];
    if (nextElement) {
      const catRadioElement = nextElement.radioFieldParentWrapper;
      catRadioElement.setAttribute('tabindex', '0');
      catRadioElement.focus();
      if (!nextElement.readOnly) {
        this.unCheckAll();
        nextElement.checked = true;
        this.dispatchRadioChangeEvent();
      }
    }
  }

  /**
   * 1) Find current active radio button and move the active status to next radio button if current active
   * 2) Check whether next radio is disabled or not if it is disabled move active state to next enabled radio button
   */
  private findNextRadioButtonIndex(): number {
    const radioGroupLength = this.getAllNamedSiblings.length;
    /**1 */
    let index = this.getAllNamedSiblings.indexOf(this);
    if (radioGroupLength > index) {
      index = index + 1;
    }
    if (radioGroupLength == index) {
      index = 0;
    }
    /**2 */
    if (this.getAllNamedSiblings[index]) {
      while (this.getAllNamedSiblings[index].disabled) {
        if (radioGroupLength > index) {
          index = index + 1;
        }
        if (radioGroupLength === index) {
          index = 0;
        }
      }
    }
    return index;
  }

  /**
   * 1) To prevent toggling of readonly radio field in browse/virtual mode of screen reader
   */
  private handleOnChange(): void {
    if (this.readOnly || this.disabled) {
      this.shadowRoot!.querySelector('input')!.checked = this.checked; /* 1 */
    }
  }

  private hasFieldNote() {
    return this.fieldNote || this.errorNote || this.warningNote || this.hasFieldNoteSlot || this.hasErrorSlot || this.hasWarningSlot;
  }

  /**
   * Display error note from slot or from errorNote property
   */
  private getErrorNote() {
    return html`
      <cat-icon-warning-diamond-filled
        part="error-icon"
        exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
        color="error"
        .iconTitle=${this.errorIconTitle}
        class="cat-c-radio-field__icon"
      ></cat-icon-warning-diamond-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-radio--fieldnote cat-c-radio-field__error-note"
        ?isDisabled=${this.disabled}
        .status=${'error'}
      >
        <slot part="part-error" name="error">${this.errorNote}</slot>
      </cat-field-note>
    `;
  }

  /**
   * Display warning note from slot or from warningNote property
   */

  private getWarningNote() {
    return html`
      <cat-icon-warning-triangle-filled
        part="warning-icon"
        exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
        color="knockout-brand"
        .iconTitle=${this.warningIconTitle}
        class="cat-c-radio-field__icon"
      ></cat-icon-warning-triangle-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-radio--fieldnote cat-c-radio-field__warning-note"
        ?isDisabled=${this.disabled}
        .status=${'warning'}
      >
        <slot part="part-warning" name="warning">${this.warningNote}</slot>
      </cat-field-note>
    `;
  }

  private getFieldNote() {
    return html`<cat-field-note
      part="field-note-wrapper"
      exportparts=${this.getExportParts('field-note')}
      class="cat-c-radio--fieldnote cat-c-radio-field__field-note"
      ?isDisabled=${this.disabled}
      .status=${this.status}
    >
      <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
    </cat-field-note> `;
  }

  /**
   * To render loading variant
   */
  private renderLoading(componentClassNames: DirectiveResult<typeof ClassMapDirective>): TemplateResult<1> {
    return html` <div part="base" class="${componentClassNames}">
      <div part="container" class="cat-c-radio-field__container">
        <div part="input" class="cat-c-radio__input"></div>
        <span part="custom-radio" class="cat-c-radio__custom-radio"></span>
        <cat-skeleton
          part="loader"
          exportparts=${this.getExportParts('skeleton')}
          class="cat-c-radio-field-loading"
          width="100px"
          size="sm"
        ></cat-skeleton>
      </div>
      ${this.hasFieldNote()
        ? html`
            <div part="field-note-loader" class="cat-c-radio-field__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                arialabel=""
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width="100px"
              ></cat-skeleton>
            </div>
          `
        : nothing}
    </div>`;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-radio-field', {
      'cat-is-disabled': this.disabled,
      'cat-is-active': this.checked === true,
      'cat-is-error': this.status === 'error',
      'cat-is-readonly': this.readOnly,
      'cat-is-loading': this.isLoading,
      'cat-is-inline': this.isInline === true,
      'cat-c-radio--hidden-label': this.hideLabel,
      'cat-c-radio-readonly': this.readOnly,
      'cat-c-radio-field--has-error': (this.errorNote || this.hasErrorSlot) && this.status === 'error',
      'cat-c-radio-field--has-warning': (this.warningNote || this.hasWarningSlot) && this.status === 'warning',
      'cat-c-radio-field--has-field-note': (this.fieldNote || this.hasFieldNoteSlot) && this.status !== 'error' && this.status !== 'warning'
    });

    if (this.isLoading) {
      return this.renderLoading(componentClassNames);
    }
    return html`
      <div
        part="base"
        class="${componentClassNames}"
        @click=${!this.checked && !this.disabled && !this.readOnly ? this.handleRadioSelection : nothing}
        role="radio"
        aria-checked=${this.checked}
        tabindex=${this.disabled ? '-1' : '0'}
        aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
        aria-disabled="${ifDefined(this.disabled || this.readOnly)}"
        @keydown=${this.handleKeyDown}
        @keyup=${this.handleKeyUp}
      >
        <div
          part="container"
          class=${this.radioPressed ? 'cat-c-radio-field__container cat-c-radio-field__container-active' : 'cat-c-radio-field__container'}
        >
          <input
            part="input"
            tabindex="-1"
            id="${ifDefined(this.fieldId)}"
            type="radio"
            name="${ifDefined(this.name)}"
            class="cat-c-radio__input"
            .value="${this.value ?? ''}"
            .checked="${this.checked}"
            ?disabled="${this.disabled}"
            @change=${this.handleOnChange}
          />
          <!-- This span is added because the input radio class is not taking scss changes. So, applied scss to this new span class -->
          <span part="custom-wrapper" class="cat-c-radio__custom-wrapper"></span>
          <span part="custom-radio" class="cat-c-radio__custom-radio"></span>
          <label part="part-label" class="cat-c-radio-field__label" for="${ifDefined(this.fieldId)}">
            <slot part="part-default">${this.label}</slot>
            <span part="part-notation" class="cat-c-radio-field__notation">${this.notation}</span>
          </label>
        </div>
        <div class="cat-c-radio-field__fieldnote-container">
          <!--- field note -->
          ${this.getFieldNote()}
          <!--- error note -->
          ${this.getErrorNote()}
          <!--- warning note -->
          ${this.getWarningNote()}
        </div>
      </div>
    `;
  }
}

if (customElements.get('cat-radio-field') === undefined) {
  customElements.define('cat-radio-field', BlocksRadioField);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-radio-field': BlocksRadioField;
  }
}
